<template>
  <div class="unit-page">
    <div class="page-header-mobile d-flex align-items-center justify-content-between mb-3">
      <h2 class="mb-0 page-title-mobile">Gestión de Unidades de Medida</h2>
      <button class="btn btn-dark btn-mobile-primary" @click="openCreate">
        <i class="bi bi-plus-lg me-1"></i> Nueva Unidad
      </button>
    </div>

    <div class="mobile-context-chip d-lg-none">
      <span class="mobile-context-label">Catálogo e Inventario</span>
      <strong>Unidades</strong>
    </div>

    <section class="policy-panel mb-3" aria-label="Política de unidades de medida">
      <div>
        <p class="policy-eyebrow">Unidad operativa de stock</p>
        <h3>Usar sólo magnitudes reales</h3>
        <p>
          Unidad, gramos y kilogramos pertenecen a unidades de medida. Caja, pack, blister, tira y combo pertenecen a presentaciones.
        </p>
      </div>
      <div class="policy-summary">
        <span class="policy-pill ok">{{ auditSummary.OPERATIVA || 0 }} operativas</span>
        <span class="policy-pill legacy">{{ auditSummary.PRESENTACION_LEGACY || 0 }} legacy</span>
        <span class="policy-pill review">{{ auditSummary.REVISAR || 0 }} revisar</span>
      </div>
    </section>

    <div class="card d-none d-lg-block">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr>
              <th>Nombre</th>
              <th style="width: 170px;">Clasificación</th>
              <th>Descripción</th>
              <th style="width: 200px;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4" class="text-center py-4">Cargando...</td>
            </tr>
            <tr v-else-if="error">
              <td colspan="4" class="text-danger py-4">{{ error }}</td>
            </tr>
            <tr v-else v-for="u in units" :key="unitKey(u)">
              <td>{{ u.nombre }}</td>
              <td>
                <span :class="['unit-policy-badge', policyBadgeClass(u)]">
                  {{ policyLabel(u) }}
                </span>
              </td>
              <td>{{ u.descripcion }}</td>
              <td>
                <button class="btn btn-outline-secondary btn-sm me-2" @click="openEdit(u)">Editar</button>
                <button class="btn btn-outline-danger btn-sm" @click="remove(u)">Eliminar</button>
              </td>
            </tr>
            <tr v-if="!loading && !error && units.length === 0">
              <td colspan="4" class="text-center py-4">Sin unidades</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        class="px-3 pb-3"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-changed="handlePageChange"
      />
    </div>

    <div class="mobile-entity-list d-lg-none">
      <div v-if="loading" class="mobile-empty-state">
        <i class="bi bi-arrow-clockwise spin"></i>
        <span>Cargando unidades...</span>
      </div>
      <div v-else-if="error" class="mobile-empty-state error">
        <i class="bi bi-exclamation-triangle"></i>
        <span>{{ error }}</span>
      </div>
      <div v-else-if="units.length === 0" class="mobile-empty-state">
        <i class="bi bi-rulers"></i>
        <span>Sin unidades</span>
      </div>
      <div v-else v-for="u in units" :key="`mobile-${unitKey(u)}`" class="mobile-entity-card">
        <div class="mobile-entity-top">
          <div>
            <p class="mobile-entity-label">Unidad</p>
            <h3 class="mobile-entity-title">{{ u.nombre }}</h3>
            <span :class="['unit-policy-badge', policyBadgeClass(u)]">
              {{ policyLabel(u) }}
            </span>
          </div>
          <span class="mobile-entity-id">#{{ unitId(u) }}</span>
        </div>
        <p class="mobile-entity-description">
          {{ u.descripcion || 'Sin descripción' }}
        </p>
        <div class="mobile-entity-actions">
          <button class="btn btn-outline-secondary btn-sm" @click="openEdit(u)">Editar</button>
          <button class="btn btn-outline-danger btn-sm" @click="remove(u)">Eliminar</button>
        </div>
      </div>
      <AppPagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-changed="handlePageChange"
      />
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>
    <div
      v-if="showModal"
      class="modal fade show d-block"
      id="unidad-modal"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-lg-down">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? 'Editar Unidad' : 'Nueva Unidad' }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent>
              <div class="mb-3">
                <label class="form-label">Nombre</label>
                <input
                  v-model.trim="form.nombre"
                  type="text"
                  :class="['form-control', { 'is-invalid': formPolicyBlocksSave }]"
                  placeholder="Nombre de la unidad"
                />
                <div v-if="formPolicyBlocksSave" class="invalid-feedback d-block">
                  {{ formPolicyMessage }}
                </div>
                <small v-else class="text-muted">
                  Valores esperados: unidad, g o kg.
                </small>
              </div>
              <div class="mb-3">
                <label class="form-label">Descripción</label>
                <textarea
                  v-model.trim="form.descripcion"
                  class="form-control"
                  rows="3"
                  placeholder="Descripción"
                />
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeModal">Cancelar</button>
            <button class="btn btn-primary" :disabled="saving || !isFormValid" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ isEditing ? 'Guardar cambios' : 'Crear' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppPagination from '@/components/AppPagination.vue';
import api from '@/services/apiService.js';

export default {
  name: 'UnidadMedidaView',
  components: { AppPagination },
  data() {
    return {
      units: [],
      loading: false,
      error: '',
      showModal: false,
      isEditing: false,
      saving: false,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      auditSummary: {},
      auditById: new Map(),
      presentationTerms: new Set([
        'caja',
        'cajas',
        'paquete',
        'paquetes',
        'pack',
        'packs',
        'blister',
        'blisters',
        'tira',
        'tiras',
        'combo',
        'combos',
        'lata',
        'latas',
        'botella',
        'botellas',
        'bolsa',
        'bolsas',
        'frasco',
        'frascos',
        'sobre',
        'sobres',
        'display',
        'displays',
        'docena',
        'docenas'
      ]),
      operationalAliases: new Set([
        'unidad',
        'unidades',
        'unid',
        'unid.',
        'u',
        'g',
        'gr',
        'gr.',
        'gramo',
        'gramos',
        'kg',
        'kilo',
        'kilos',
        'kilogramo',
        'kilogramos'
      ]),
      form: {
        id: null,
        nombre: '',
        descripcion: ''
      }
    };
  },
  watch: {
    showModal: {
      immediate: true,
      handler(val) {
        try {
          if (typeof document !== 'undefined' && document?.body) {
            if (val) document.body.classList.add('modal-open');
            else document.body.classList.remove('modal-open');
          }
        } catch (e) {
          console.debug('Modal toggle failed', e);
        }
      }
    }
  },
  beforeUnmount() {
    try {
      if (typeof document !== 'undefined' && document?.body) {
        document.body.classList.remove('modal-open');
      }
    } catch (e) {
      console.debug('Modal cleanup failed', e);
    }
  },
  computed: {
    isFormValid() {
      return this.form.nombre && this.form.nombre.length >= 1 && !this.formPolicyBlocksSave;
    },
    formPolicy() {
      return this.classifyName(this.form.nombre);
    },
    formPolicyBlocksSave() {
      return Boolean(this.form.nombre) && !this.formPolicy.es_operativa;
    },
    formPolicyMessage() {
      return this.formPolicy.mensaje || 'Unidad no permitida por política operativa.';
    }
  },
  async mounted() {
    await this.fetchAudit();
    await this.fetchUnits();
  },
  methods: {
    normalizeName(value = '') {
      return String(value)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    },
    classifyName(value = '') {
      const normalized = this.normalizeName(value);
      if (!normalized) {
        return { tipo_uso: 'INVALIDA', es_operativa: false, mensaje: 'Ingresá una unidad de medida.' };
      }
      if (this.operationalAliases.has(normalized)) {
        return { tipo_uso: 'OPERATIVA', es_operativa: true, mensaje: null };
      }
      if (this.presentationTerms.has(normalized)) {
        return {
          tipo_uso: 'PRESENTACION_LEGACY',
          es_operativa: false,
          mensaje: 'Ese término corresponde a una presentación comercial.'
        };
      }
      return {
        tipo_uso: 'REVISAR',
        es_operativa: false,
        mensaje: 'Unidad no reconocida por la política operativa actual.'
      };
    },
    unitId(u) {
      return u.unidad_medida_id ?? u.id ?? this.unitKey(u);
    },
    unitKey(u) {
      return `${u.unidad_medida_id ?? u.id ?? u.nombre}`;
    },
    policyForUnit(unit) {
      const id = this.unitId(unit);
      return unit.politica_unidad_medida || this.auditById.get(Number(id)) || this.classifyName(unit.nombre);
    },
    policyLabel(unit) {
      const type = this.policyForUnit(unit)?.tipo_uso;
      if (type === 'OPERATIVA') return 'Operativa';
      if (type === 'PRESENTACION_LEGACY') return 'Legacy';
      if (type === 'REVISAR') return 'Revisar';
      return 'Inválida';
    },
    policyBadgeClass(unit) {
      const type = this.policyForUnit(unit)?.tipo_uso;
      if (type === 'OPERATIVA') return 'is-operational';
      if (type === 'PRESENTACION_LEGACY') return 'is-legacy';
      return 'is-review';
    },
    async fetchAudit() {
      try {
        const { data } = await api.get('/api/measure-units/audit/operational-policy');
        const audit = data?.data ?? data;
        const units = Array.isArray(audit?.unidades) ? audit.unidades : [];
        this.auditSummary = audit?.resumen || {};
        this.auditById = new Map(units.map((unit) => [
          Number(unit.unidad_medida_id ?? unit.id),
          unit.politica_unidad_medida
        ]));
      } catch (e) {
        console.warn('No se pudo cargar auditoría de unidades', e);
        this.auditSummary = {};
        this.auditById = new Map();
      }
    },
    async fetchUnits() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await api.get('/api/measure-units', { page: this.currentPage, limit: this.pageSize });
        let list = [];
        let pagination = null;
        if (Array.isArray(data)) {
          list = data;
        } else if (Array.isArray(data?.data)) {
          list = data.data;
        } else if (Array.isArray(data?.data?.data)) {
          list = data.data.data;
          pagination = data.data.pagination ?? null;
        } else {
          list = [];
        }
        this.units = list;
        this.units = this.units.map((unit) => ({
          ...unit,
          politica_unidad_medida: unit.politica_unidad_medida || this.auditById.get(Number(this.unitId(unit))) || this.classifyName(unit.nombre)
        }));
        if (pagination) {
          this.currentPage = Number(pagination.page) || this.currentPage;
          this.totalPages = Number(pagination.totalPages) || 1;
          this.pageSize = Number(pagination.limit) || this.pageSize;
        } else {
          this.totalPages = 1;
        }
      } catch (e) {
        console.error(e);
        this.error = 'No se pudieron cargar las unidades';
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.fetchUnits();
    },
    openCreate() {
      this.isEditing = false;
      this.form = { id: null, nombre: '', descripcion: '' };
      this.showModal = true;
    },
    async openEdit(u) {
      this.isEditing = true;
      const id = this.unitId(u);
      try {
        const { data } = await api.get(`/api/measure-units/${id}`);
        const mu = data?.data ?? data;
        this.form = {
          id,
          nombre: mu?.nombre ?? u.nombre ?? '',
          descripcion: mu?.descripcion ?? u.descripcion ?? ''
        };
      } catch (e) {
        this.form = {
          id,
          nombre: u.nombre ?? '',
          descripcion: u.descripcion ?? ''
        };
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async save() {
      if (!this.isFormValid) return;
      this.saving = true;
      try {
        if (this.isEditing && this.form.id != null) {
          await api.put(`/api/measure-units/${this.form.id}`, {
            nombre: this.form.nombre,
            descripcion: this.form.descripcion
          });
        } else {
          await api.post('/api/measure-units', {
            nombre: this.form.nombre,
            descripcion: this.form.descripcion
          });
        }
        this.showModal = false;
        await this.fetchAudit();
        await this.fetchUnits();
      } catch (e) {
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    async remove(u) {
      const id = this.unitId(u);
      if (!id) return;
      const confirmed = await confirm({
        message: '¿Eliminar esta unidad?',
        title: 'Eliminar unidad de medida',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar'
      });
      if (!confirmed) return;
      try {
        await api.delete(`/api/measure-units/${id}`);
        await this.fetchAudit();
        await this.fetchUnits();
        alert.success('Unidad de medida eliminada.');
      } catch (e) {
        console.error(e);
        alert.error('No se pudo eliminar la unidad. Intente nuevamente.');
      }
    }
  }
};
</script>

<style scoped>
.unit-page {
  max-width: 1200px;
}

.card {
  border: 1px solid #e5e5e5;
  border-radius: .5rem;
}

th, td { vertical-align: middle; }

.policy-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #dbe3ea;
  border-left: 4px solid #0f766e;
  border-radius: 0.5rem;
  background: #ffffff;
}

.policy-panel h3 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  color: #111827;
}

.policy-panel p {
  margin: 0;
  color: #4b5563;
}

.policy-eyebrow {
  margin-bottom: 0.25rem !important;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f766e !important;
}

.policy-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
  min-width: 220px;
}

.policy-pill,
.unit-policy-badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.8rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.policy-pill.ok,
.unit-policy-badge.is-operational {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.policy-pill.legacy,
.unit-policy-badge.is-legacy {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}

.policy-pill.review,
.unit-policy-badge.is-review {
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
}

.page-title-mobile {
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 0.82rem;
}

.mobile-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.mobile-entity-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.mobile-entity-card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.mobile-entity-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 0.65rem;
}

.mobile-entity-label {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.mobile-entity-title {
  margin: 0;
  font-size: 1rem;
  color: #0f172a;
}

.mobile-entity-id {
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #111827;
  color: #fff;
  font-size: 0.76rem;
  font-weight: 700;
}

.mobile-entity-description {
  margin: 0;
  color: #475569;
}

.mobile-entity-actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.9rem;
}

.mobile-entity-actions .btn {
  flex: 1 1 0;
}

.mobile-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 1rem;
  background: #f8fafc;
  color: #64748b;
}

.mobile-empty-state.error {
  color: #b91c1c;
  border-color: #fecaca;
  background: #fef2f2;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 991.98px) {
  .page-header-mobile {
    align-items: stretch !important;
    flex-direction: column;
    gap: 0.85rem;
  }

  .btn-mobile-primary {
    width: 100%;
  }

  .policy-panel {
    align-items: flex-start;
    flex-direction: column;
  }

  .policy-summary {
    justify-content: flex-start;
    min-width: 0;
  }
}

@media (max-width: 575.98px) {
  .mobile-entity-top,
  .mobile-entity-actions {
    flex-direction: column;
  }

  .mobile-entity-id {
    align-self: flex-start;
  }
}
</style>
